import { useState } from 'react';
import VideoPlayer from '@/components/feature/VideoPlayer';
import { useLang } from '../../../hooks/useLang';
import PolicyCompare from './PolicyCompare';

// ──────────────────────────────────────────────────────────
// 数据：各地区政策
// ──────────────────────────────────────────────────────────
const policies = [
  {
    region: '香港', flag: '🇭🇰', status: '全球最友好',
    items: [
      {
        title: 'Project Ensemble 批发CBDC沙盒（第二期扩展）',
        body: '香港金管局（HKMA）推出批发式CBDC沙盒第二期，纳入跨境RWA结算测试。汇丰、渣打、中银、DBS等多家银行参与，测试代币化港元存款与外债、房产REITs等RWA资产的原子结算（DVP），是香港迈向RWA结算基础设施的重要里程碑。',
        date: '2024.03', type: '沙盒',
      },
      {
        title: 'SFC：RWA代币属证券型代币（核心通函）',
        body: 'SFC发布通函，明确RWA代币属于证券型代币，须遵循《证券及期货条例》，仅限香港专业投资者（PI≥800万港元）认购，不得向公众发售。此通函是香港RWA监管最重要的基础文件，明确了1/4/9类牌照要求和信息披露框架。',
        date: '2023.11', type: '指引',
        deepRead: true,
      },
      {
        title: 'HKEX FINI数字化新股平台正式上线',
        body: '香港交易所推出FINI平台，将IPO结算从T+5缩短至T+2，为代币化证券结算奠定基础，是香港资本市场数字化转型的重要一步。HKEX同期宣布探索数字证券（DSE）交易基础设施，与RWA发展协同推进。',
        date: '2023.11', type: '试点',
      },
      {
        title: 'VASP发牌制度正式生效',
        body: '2023年《虚拟资产服务提供商条例》正式实施，虚拟资产交易所须向证监会（SFC）申领牌照，截至2026年Q1共12家VATP持牌运营。制度明确1类（证券交易）和7类（期货合约）为RWA核心业务牌照，确立了亚洲最完善的RWA交易监管框架。',
        date: '2023.06', type: '法规',
      },
      {
        title: '香港政府《虚拟资产发展政策宣言》',
        body: '香港政务司司长主持跨部门会议，发布《虚拟资产发展政策宣言》，明确香港欢迎虚拟资产行业发展，确立以"相同业务、相同风险、相同规则"为原则的监管框架，是香港参与全球RWA竞争的顶层政策依据。',
        date: '2022.10', type: '声明',
      },
    ],
  },
  {
    region: '新加坡', flag: '🇸🇬', status: '制度完善',
    items: [
      {
        title: 'Project Guardian 第三期扩展：外汇RWA结算',
        body: 'MAS与JP摩根、DBS、星展银行联合推进，第三期扩展至外汇RWA结算场景，测试多币种跨境原子结算。同期引入SGD/USD代币化外汇对，新加坡正成为亚洲机构级RWA多资产结算的首选试验场。',
        date: '2024.06', type: '试点',
      },
      {
        title: 'MAS资本市场服务（CMS）牌照要求明确',
        body: 'MAS明确：在新加坡发行RWA代币须申领资本市场服务牌照（CMS License），代币若具有证券属性须符合《证券与期货法》第274/275条的发行豁免，基金管理须持有RFMC或LFMC牌照，基金托管须持有CMS信托牌照，为Project Guardian生态奠定法律基础，明确了各类代币的监管路径和许可要求。',
        date: '2023.12', type: '法规',
      },
      {
        title: 'MAS数字代币分类指引',
        body: '新加坡金管局（MAS）发布《数字代币发行指南》，区分支付型、证券型、实用型代币，分类监管，明确RWA代币多属证券型，须符合《证券与期货法》。为Project Guardian生态奠定法律基础，明确了各类代币的监管路径和许可要求。',
        date: '2023.07', type: '指引',
      },
    ],
  },
  {
    region: '欧盟', flag: '🇪🇺', status: 'MiCA全面生效',
    items: [
      {
        title: 'MiCA条例2024年12月全面生效',
        body: '欧盟《加密资产市场监管条例》（MiCA）全面生效，为27个成员国建立统一加密资产监管框架，资产参考代币（ART）和电子货币代币（EMT）须获CASP牌照，具备100%储备要求。标志着全球最大单一市场的RWA监管框架正式完成。',
        date: '2024.12', type: '法规',
      },
      {
        title: 'ESMA：证券代币（STO）在MiFID II框架下监管',
        body: '欧洲证券与市场管理局（ESMA）明确：证券型RWA代币不纳入MiCA，而是在MiFID II框架下监管，须在授权的DLT市场基础设施（DLT MTF）交易，与传统证券监管等同。德意志交易所、法国巴黎银行等已申领DLT MTF资质。',
        date: '2024.06', type: '指引',
      },
      {
        title: 'DLT试点机制（Pilot Regime）正式运行',
        body: '欧盟DLT试点机制于2023年3月正式运行，允许在受监控环境下测试基于DLT的证券交易和结算系统，适用于市值≤2亿欧元的债券、股票和基金份额，是欧盟RWA合规上市的重要路径，已有14家机构获批参与。',
        date: '2023.03', type: '试点',
      },
    ],
  },
  {
    region: '美国', flag: '🇺🇸', status: '审慎监管',
    items: [
      {
        title: 'OCC允许银行持有加密资产（新指引）',
        body: '美国货币监理署（OCC）发布新指引，允许联邦特许银行为客户提供加密资产托管服务，为大型银行开展RWA托管业务打开合规通道。摩根大通、花旗、富国银行等大行已基于此布局代币化资产业务，BlackRock随即推出BUIDL基金。',
        date: '2024.03', type: '指引',
      },
      {
        title: 'Reg A+豁免上限提至7500万美元',
        body: 'SEC提高Reg A+豁免发行上限至7500万美元/年，可向普通投资者发行，使中型RWA项目有了面向非合格投资者的合规通道。须提交简化招股说明书并经SEC审查，适合面向美国散户的中小型RWA项目。',
        date: '2024.01', type: '法规',
      },
      {
        title: 'SEC：豪威测试认定RWA为证券',
        body: '美国SEC坚持豪威测试（Howey Test），多数RWA代币被认定为证券，须依《证券法》注册或豁免。Reg D 506(c)豁免是美国RWA最常用的合规路径，仅限合格投资者，无募资金额上限，可公开宣传，已被Ondo Finance、Maple Finance等头部RWA项目采用。',
        date: '2023.04', type: '执法',
      },
    ],
  },
  {
    region: '中国内地', flag: '🇨🇳', status: '严格禁止',
    items: [
      {
        title: '证监会RWA试点指导意见（草案阶段）',
        body: '据可靠信源，中国证监会正研究"跨境RWA试点"相关政策，拟允许境内符合条件资产通过香港持牌机构在境外发行代币，资金回流须经CIPS/QFLP渠道。试点行业可能包括供应链金融、REITs、绿色能源、碳资产等。正式文件尚未公开发布，以官方公告为准。',
        date: '2025.Q4预期', type: '指引',
      },
      {
        title: '十部委联合通知：全面禁止虚拟货币（核心政策）',
        body: '2021年国家发改委等10部委联合发布通知，全面叫停境内虚拟货币相关业务。此通知是目前中国境内虚拟货币监管的最高级别、最全面的政策文件，至今完全有效。同期，多地开始清理比特币矿场，相关账户被要求注销，境内RWA发行的代币化通道完全关闭。',
        date: '2021.09', type: '禁令',
        deepRead: true,
      },
      {
        title: '区块链信息服务管理规定',
        body: '网信办规定：国内企业可从事联盟链技术服务，须完成区块链信息服务备案，严禁发行虚拟货币、代币或任何加密资产。合规路径唯一：境内确权→证监会备案→香港持牌机构发行。此规定奠定了中国"联盟链合规、公链禁止"的基本制度格局。',
        date: '2019.01', type: '规定',
      },
    ],
  },
];

// ──────────────────────────────────────────────────────────
// 深度精读数据
// ──────────────────────────────────────────────────────────
const sfcCircularArticles = [
  {
    label: '核心定性',
    quote: '涉及真实世界资产的代币（如代表不动产、证券、基金份额的代币）通常构成《证券及期货条例》下的"集体投资计划"权益或"证券"，不论其以代币形式还是传统形式发行，法律定性相同。',
    analysis: '【逐句精读】"通常构成"—并非绝对，须就具体代币结构进行法律分析。"不论其以代币形式"—即区块链技术不改变资产的法律性质，SFC以"同样业务、同样规则"原则穿透技术外壳。这条是RWA代币"必须按证券监管"的法律根基。',
  },
  {
    label: '持牌要求',
    quote: '在香港就RWA代币向投资者提供意见（第4类牌照）、经营买卖（第1类牌照）或管理投资组合（第9类牌照），均须就相关活动持有SFC牌照或豁免。',
    analysis: '【逐句精读】此条划定了持牌边界：①推荐RWA需4类牌；②交易/撮合需1类牌；③基金管理需9类牌。实务中，完整的RWA发行通常需要发行方或其顾问同时持有或委托持有1类+4类+9类牌照，三牌缺一不可。',
  },
  {
    label: '投资者限制',
    quote: '证券型RWA代币的发行、销售及推广，只可向香港《证券及期货条例》附表1所定义的专业投资者进行，任何向非专业投资者发售或推广的行为属违法。',
    analysis: '【逐句精读】"专业投资者"门槛（个人≥800万港币净资产）是RWA在港发行的核心护城河。"任何向非专业投资者发售"包括：在社交媒体宣传代币、向普通投资者发送招募文件、在公开平台展示预期回报等，均属违法范畴。',
  },
  {
    label: '信息披露',
    quote: '发行人或其代理人须向投资者提供充分、准确的信息披露文件，内容须涵盖：底层资产性质与估值方法、代币合约技术风险、发行人法律结构、流动性风险及退出机制。',
    analysis: '【逐句精读】SFC要求的信息披露包含四大模块：①资产端（底层资产的独立估值报告、租金/收益预测模型）；②技术端（智能合约审计报告）；③法律端（SPV架构法律意见书）；④流动性端（二级市场流动性方案或锁定期说明）。四项缺一均构成不充分披露。',
  },
  {
    label: '托管要求',
    quote: '证券型RWA代币的底层资产及代币本身须由独立的合规托管人负责保管，代币发行人不得自行托管，托管机构须为SFC认可或符合条件的银行/信托机构。',
    analysis: '【逐句精读】"独立的合规托管人"是防范发行人卷款跑路的关键制度安排。实务中，底层资产（如物业所有权文件）通常由香港信托公司保管，代币私钥由持牌机构托管（如HashKey Custody），双重独立托管缺失是SFC最常见的拒发牌原因之一。',
  },
  {
    label: 'KYC/AML强制要求',
    quote: '所有参与RWA代币发行与交易的持牌机构须严格执行KYC（了解你的客户）及AML（反洗钱）程序，包括核实投资者身份、来源资金的合法性审查，并按《打击洗钱及恐怖分子资金筹集条例》（AMLO）要求保存记录不少于7年。',
    analysis: '【逐句精读】AMLO要求是香港RWA合规成本最高的环节之一：①身份核实：护照+地址证明+自拍（活体检测）；②资金来源：近3个月银行流水+资产证明；③持续监控：对高风险客户须强化尽职调查（EDD）；④记录保存：7年存档要求意味着持牌机构须建立可审计的KYC档案系统。',
  },
];

const chinaNoticeArticles = [
  {
    label: '第一条 虚拟货币法律性质',
    quote: '虚拟货币不具有与法定货币等同的法律地位。比特币、以太币、泰达币等虚拟货币不具有法偿性，不应且不能作为货币在市场上流通使用。',
    analysis: '【精读意义】这是从根本上否定了虚拟货币的货币属性。"不具有法偿性"意味着任何人有权拒绝以虚拟货币作为支付手段，法院不保护以虚拟货币计价的合同。对RWA的影响：在境内以任何虚拟货币计价的RWA均无法获得法律保护。',
  },
  {
    label: '第二条 禁止境内经营',
    quote: '境内禁止开展法定货币与虚拟货币兑换业务、虚拟货币之间的兑换业务、作为中央对手方买卖虚拟货币、为虚拟货币交易提供信息中介和定价服务、代币发行融资（ICO）以及虚拟货币衍生品交易等业务。',
    analysis: '【精读意义】"代币发行融资"直接涵盖所有形式的RWA代币境内发行。"信息中介和定价服务"甚至包括为境外RWA提供估值服务的境内公司，影响极为广泛。唯一豁免路径：仅提供底层资产确权、法律咨询、IT开发等不涉及代币发行的服务。',
  },
  {
    label: '第三条 境外平台境内服务禁止',
    quote: '境外虚拟货币交易所通过互联网向我国境内居民提供服务同样属于违法金融活动。对于相关境外机构的工作人员，以及明知或应知其从事虚拟货币相关业务仍为其提供营销宣传、支付结算、技术支持等服务的法人、非法人组织和自然人，依法追究法律责任。',
    analysis: '【精读意义】此条具有域外效力！即使是香港合规的RWA平台，若向境内居民提供服务（含微信营销、境内开说明会、境内银行转账购币）也构成违法。"明知或应知"标准意味着为境外RWA提供宣传的境内KOL、微信公众号也可能被追责。',
  },
  {
    label: '第四条 金融机构禁止参与',
    quote: '金融机构和非银行支付机构不得为虚拟货币相关业务活动提供账户开立、登记、交易、清算、结算等产品或服务，不得承保虚拟货币相关业务或为其提供保险服务。',
    analysis: '【精读意义】这条切断了RWA在境内的金融基础设施支撑。实务影响：①境内银行不得为RWA相关SPV开户；②支付宝/微信支付不得处理代币购买款项；③境内保险公司不得为RWA资产承保。因此境内RWA项目须100%通过香港法律实体操作资金。',
  },
  {
    label: '第五条 投资者风险自担声明',
    quote: '参与虚拟货币投资交易活动存在法律风险。任何法人、非法人组织和自然人投资虚拟货币及相关衍生品，违背公序良俗的，相关民事法律行为无效，由此引发的损失由其自行承担。',
    analysis: '【精读意义】"相关民事法律行为无效"是核心：如果境内投资者以人民币购买境内发行的RWA代币，该合同因违反公序良俗而无效，投资者的损失法院不予保护，这构成境内RWA欺诈的最高风险警示。',
  },
];

const sfcChecklist = [
  { item: 'SFC 20条强化合规自查清单（RWA发行方必读）', level: '必须' },
  // 资产端
  { item: '底层资产权属清晰：独立律师出具产权无瑕疵意见书（含抵押/查封/纠纷核查）', level: '必须' },
  { item: '底层资产估值：独立估值机构（Big4或认可机构）出具资产评估报告，至少每半年更新', level: '必须' },
  { item: '真实出售确认：律师出具"真实出售意见书"，确保资产与发起人破产隔离', level: '必须' },
  { item: '资产保险：底层实物资产（房产/设备/商品）须投保，保单转让给SPV', level: '必须' },
  // 架构端
  { item: '香港SPV结构合法设立（开曼群岛/BVI公司或香港有限合伙），出具有效注册文件', level: '必须' },
  { item: 'SPV年度审计：委托Big4或认可会计师事务所完成SPV年度审计报告', level: '必须' },
  { item: '管理公司（GP/管理人）具有合规资质，如为香港公司须确认无违规记录', level: '必须' },
  // 持牌机构端
  { item: '委托SFC持牌1类机构作为分销商/承销商，取得委托协议', level: '必须' },
  { item: '委托SFC持牌9类机构作为资产管理人，签署资产管理协议', level: '必须' },
  { item: '委托SFC持牌4类机构（如有投资建议）提供投资意见书', level: '建议' },
  // 托管端
  { item: '底层资产独立托管：实物资产文件（产权证/合同）由持牌信托公司保管', level: '必须' },
  { item: '代币私钥独立托管：数字资产私钥由SFC认可的独立托管机构（如HashKey Custody）保管', level: '必须' },
  // 投资者端
  { item: '全部认购人已完成专业投资者认定（≥800万港元净资产），留存认定文件7年', level: '必须' },
  { item: 'KYC/AML完成并存档：护照+地址证明+资金来源文件，按AMLO保存7年', level: '必须' },
  { item: 'FATF旅行规则合规：使用Notabene/VerifyVASP等工具确保超1000美元转账信息随行', level: '必须' },
  // 信息披露端
  { item: '招股说明书/信息披露文件已获持牌机构审核，含底层资产、SPV结构、技术风险、流动性风险', level: '必须' },
  { item: '投资者持续披露机制：至少每季度向投资者发送NAV更新和资产运营报告', level: '必须' },
  // 技术端
  { item: '智能合约经第三方安全审计（CertiK/Hacken/Quantstamp等），审计报告公开可查', level: '必须' },
  { item: '合约含合规白名单（仅PI钱包可转账）、锁定期、可暂停机制，并经法律确认合规', level: '必须' },
  { item: '退出机制明确：赎回流程、流动性提供方安排、清算程序均已写入合约和招股书', level: '建议' },
];

const typeColors: Record<string, string> = {
  法规: 'bg-[#7C3AED]/10 text-[#7C3AED]',
  指引: 'bg-[#F59E0B]/15 text-[#92610A]',
  沙盒: 'bg-emerald-50 text-emerald-700',
  试点: 'bg-violet-50 text-violet-700',
  执法: 'bg-red-50 text-red-600',
  声明: 'bg-slate-100 text-slate-600',
  禁令: 'bg-red-100 text-red-700',
  规定: 'bg-gray-100 text-gray-600',
};

// ──────────────────────────────────────────────────────────
// 组件
// ──────────────────────────────────────────────────────────
export default function PolicySection() {
  const [activeRegion, setActiveRegion] = useState('香港');
  const [deepReadOpen, setDeepReadOpen] = useState<string | null>(null);
  const [activeHKTab, setActiveHKTab] = useState<'overview' | 'sfcDeep' | 'cnDeep' | 'checklist'>('overview');
  const [showCompare, setShowCompare] = useState(false);
  const active = policies.find((p) => p.region === activeRegion)!;
  const { t } = useLang();

  return (
    <section id="policy" className="py-20 bg-[#F5F3FF] dark:bg-gray-900">
      {showCompare && <PolicyCompare onClose={() => setShowCompare(false)} />}
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#7C3AED]/10 dark:bg-[#7C3AED]/20 rounded-full mb-4">
            <i className="ri-global-line text-[#7C3AED] text-sm"></i>
            <span className="text-[#7C3AED] text-sm font-semibold">{t('03 · 全球政策法规库', '03 · Global Policy & Regulation Library')}</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-black text-gray-900 dark:text-white mb-4">{t('全球 RWA 监管政策深度解读', 'In-Depth Analysis of Global RWA Regulatory Policies')}</h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto text-base">
            {t(
              '追踪香港、新加坡、欧盟、美国、中国内地的最新RWA监管动态。核心政策附原文引用与逐句精读，100%可溯源。',
              'Tracking the latest RWA regulatory developments in Hong Kong, Singapore, EU, US, and mainland China. Core policies include original text citations and line-by-line analysis — 100% traceable.'
            )}
          </p>
          {/* Compare Button */}
          <button
            onClick={() => setShowCompare(true)}
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-gray-800 border-2 border-[#7C3AED] text-[#7C3AED] rounded-full font-bold text-sm hover:bg-[#7C3AED] hover:text-white transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-scales-3-line"></i>
            {t('横向对比各地区监管框架', 'Compare Regulatory Frameworks')}
            <span className="text-xs px-1.5 py-0.5 bg-[#7C3AED]/10 rounded-full font-medium">5地区</span>
          </button>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { num: '5', label: t('主要监管辖区', 'Major Jurisdictions'), icon: 'ri-earth-line' },
            { num: '20+', label: t('政策文件收录', 'Policy Documents'), icon: 'ri-file-text-line' },
            { num: '12家', label: t('香港持牌VATP', 'HK Licensed VATPs'), icon: 'ri-building-4-line' },
            { num: '2026.03', label: t('数据更新日期', 'Data Updated'), icon: 'ri-refresh-line' },
          ].map((s) => (
            <div key={s.label} className="bg-white dark:bg-gray-800 border border-[#7C3AED]/15 dark:border-[#7C3AED]/30 rounded-2xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#7C3AED]/10 dark:bg-[#7C3AED]/20 rounded-xl shrink-0">
                <i className={`${s.icon} text-[#7C3AED] text-lg`}></i>
              </div>
              <div>
                <div className="text-xl font-black text-gray-900 dark:text-white leading-none">{s.num}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Video Player */}
        <div className="mb-10">
          <VideoPlayer
            title={t('「全球RWA政策解读」专题视频', '"Global RWA Policy Analysis" Special Report')}
            subtitle={t('新闻级专题报道 · 全球主要司法管辖区监管框架', 'News-grade special report · Regulatory frameworks of major global jurisdictions')}
            duration={t('约12分钟', 'About 12 min')}
            heightClass="h-56"
            theme="dark"
            className="border border-[#7C3AED]/30"
          />
        </div>

        {/* Region Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {policies.map((p) => (
            <button
              key={p.region}
              onClick={() => { setActiveRegion(p.region); setDeepReadOpen(null); setActiveHKTab('overview'); }}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeRegion === p.region
                  ? 'bg-[#7C3AED] text-white shadow-md'
                  : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-[#7C3AED] hover:text-[#7C3AED]'
              }`}
            >
              <span>{p.flag}</span>
              <span>{p.region}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeRegion === p.region ? 'bg-white/20 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                {p.status}
              </span>
            </button>
          ))}
        </div>

        {/* ── 香港 + 中国内地 有深度精读子Tab ── */}
        {(activeRegion === '香港' || activeRegion === '中国内地') && (
          <div className="flex flex-wrap gap-2 mb-6 justify-center">
            <button
              onClick={() => setActiveHKTab('overview')}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${activeHKTab === 'overview' ? 'bg-[#7C3AED] text-white' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-[#7C3AED]'}`}
            >
              {t('政策速览', 'Policy Overview')}
            </button>
            {activeRegion === '香港' && (
              <>
                <button
                  onClick={() => setActiveHKTab('sfcDeep')}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${activeHKTab === 'sfcDeep' ? 'bg-[#7C3AED] text-white' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-[#7C3AED]'}`}
                >
                  {t('SFC通函原文精读', 'SFC Circular Deep Read')}
                </button>
                <button
                  onClick={() => setActiveHKTab('checklist')}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${activeHKTab === 'checklist' ? 'bg-[#F59E0B] text-white' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-[#F59E0B]'}`}
                >
                  {t('SFC合规自查清单', 'SFC Compliance Checklist')}
                </button>
              </>
            )}
            {activeRegion === '中国内地' && (
              <button
                onClick={() => setActiveHKTab('cnDeep')}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${activeHKTab === 'cnDeep' ? 'bg-red-600 text-white' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-red-400'}`}
              >
                {t('十部委通知原文精读', '10-Ministry Notice Deep Read')}
              </button>
            )}
          </div>
        )}

        {/* ── 政策速览 ── */}
        {activeHKTab === 'overview' && (
          <div className="space-y-4">
            {active.items.map((item, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-6 hover:border-[#7C3AED]/30 transition-all">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xl">{active.flag}</span>
                    <h4 className="font-black text-gray-800 dark:text-white">{item.title}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[item.type] || 'bg-gray-100 text-gray-600'}`}>{item.type}</span>
                    {(item as any).deepRead && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-[#7C3AED] text-white animate-pulse">{t('含原文精读', 'Includes Deep Read')}</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 font-medium shrink-0">{item.date}</span>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{item.body}</p>
                {(item as any).deepRead && (
                  <button
                    onClick={() => setActiveHKTab(activeRegion === '香港' ? 'sfcDeep' : 'cnDeep')}
                    className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-[#7C3AED] hover:text-[#5B21B6] cursor-pointer"
                  >
                    <i className="ri-book-open-line"></i>
                    {t('查看原文逐句精读 →', 'View Line-by-Line Analysis →')}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── SFC通函原文精读 ── */}
        {activeHKTab === 'sfcDeep' && (
          <div className="space-y-6">
            <div className="bg-[#7C3AED]/5 dark:bg-[#7C3AED]/15 border border-[#7C3AED]/20 rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 flex items-center justify-center bg-[#7C3AED] rounded-lg">
                  <i className="ri-file-text-fill text-white text-sm"></i>
                </div>
                <div>
                  <div className="font-black text-gray-900 dark:text-white text-sm">{t('香港证监会（SFC）通函', 'Hong Kong SFC Circular')}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{t('《有关证券型代币发行的通函》| 发布日期：2023年11月 | 来源：www.sfc.hk', '"Circular on Security Token Offerings" | Published: November 2023 | Source: www.sfc.hk')}</div>
                </div>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                {t(
                  '本通函由SFC正式发布，是香港证券型代币（含RWA代币）监管的核心指引文件。以下为核心条款原文引用及逐句专业解析：',
                  'This circular was officially issued by the SFC and is the core guidance document for regulating security tokens (including RWA tokens) in Hong Kong. The following are key clause citations and professional line-by-line analysis:'
                )}
              </p>
            </div>

            {sfcCircularArticles.map((art, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl overflow-hidden">
                <div className="flex items-center gap-3 px-5 py-3 bg-[#7C3AED]/5 dark:bg-[#7C3AED]/15 border-b border-[#7C3AED]/10">
                  <span className="w-6 h-6 flex items-center justify-center bg-[#7C3AED] text-white text-xs font-black rounded-full shrink-0">{i + 1}</span>
                  <span className="font-black text-[#7C3AED] text-sm">{art.label}</span>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <div className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <i className="ri-double-quotes-l text-[#7C3AED]"></i> {t('原文引用', 'Original Text')}
                    </div>
                    <blockquote className="bg-[#7C3AED]/5 dark:bg-[#7C3AED]/10 border-l-4 border-[#7C3AED] rounded-r-xl px-4 py-3 text-sm text-gray-800 dark:text-gray-200 leading-relaxed font-medium italic">
                      {art.quote}
                    </blockquote>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <i className="ri-lightbulb-flash-line text-amber-500"></i> {t('专业解析', 'Professional Analysis')}
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/40 rounded-xl px-4 py-3 text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
                      {art.analysis}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              <i className="ri-information-line mr-1 text-[#7C3AED]"></i>
              {t('以上内容基于SFC官方通函整理，原始文件见：', 'The above content is based on the official SFC circular. Original document at: ')}<a href="https://www.sfc.hk" target="_blank" rel="nofollow noopener" className="text-[#7C3AED] underline">www.sfc.hk</a>{t('。如有法律效力疑问，请以SFC官方原文为准，并咨询持牌香港律师。', '. For legal validity questions, refer to the official SFC text and consult a licensed Hong Kong lawyer.')}
            </div>
          </div>
        )}

        {/* ── 十部委通知原文精读 ── */}
        {activeHKTab === 'cnDeep' && (
          <div className="space-y-6">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 flex items-center justify-center bg-red-600 rounded-lg">
                  <i className="ri-file-text-fill text-white text-sm"></i>
                </div>
                <div>
                  <div className="font-black text-gray-900 dark:text-white text-sm">{t('《关于进一步防范和处置虚拟货币交易炒作风险的通知》', '"Notice on Further Preventing and Disposing of Risks from Virtual Currency Trading Speculation"')}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {t('发布机关：中国人民银行等十部委 | 发布日期：2021年9月24日', 'Issuing Authority: People\'s Bank of China and 9 other ministries | Published: September 24, 2021')}
                  </div>
                </div>
              </div>
              <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed font-medium">
                {t('⚠️ 本通知为中国境内虚拟货币监管最高级别政策文件，截至2026年3月完全有效，无任何豁免条款。', '⚠️ This notice is the highest-level policy document for virtual currency regulation in China, fully effective as of March 2026 with no exemption clauses.')}
              </p>
            </div>

            {chinaNoticeArticles.map((art, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl overflow-hidden">
                <div className="flex items-center gap-3 px-5 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-100 dark:border-red-800/30">
                  <span className="w-6 h-6 flex items-center justify-center bg-red-600 text-white text-xs font-black rounded-full shrink-0">{i + 1}</span>
                  <span className="font-black text-red-700 dark:text-red-400 text-sm">{art.label}</span>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <div className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <i className="ri-double-quotes-l text-red-500"></i> {t('原文引用', 'Original Text')}
                    </div>
                    <blockquote className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-r-xl px-4 py-3 text-sm text-gray-800 dark:text-gray-200 leading-relaxed font-medium italic">
                      {art.quote}
                    </blockquote>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <i className="ri-lightbulb-flash-line text-amber-500"></i> {t('专业解析', 'Professional Analysis')}
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/40 rounded-xl px-4 py-3 text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
                      {art.analysis}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-xl p-5 flex gap-3">
              <i className="ri-error-warning-fill text-red-500 text-xl shrink-0 mt-0.5"></i>
              <div>
                <h4 className="font-black text-red-700 dark:text-red-400 mb-1">{t('唯一合规出境路径', 'The Only Compliant Exit Path')}</h4>
                <p className="text-red-600 dark:text-red-300 text-sm leading-relaxed">
                  {t('境内资产RWA化的唯一合规路径：', 'The only compliant path for RWA-izing domestic assets: ')}
                  <strong>{t('境内资产确权', 'Domestic Asset Title Confirmation')}</strong> →
                  <strong>{t('中国证监会跨境备案', 'CSRC Cross-border Filing')}</strong> →
                  <strong>{t('香港SPV（开曼/BVI）', 'HK SPV (Cayman/BVI)')}</strong> →
                  <strong>{t('SFC持牌机构承销', 'SFC-Licensed Institution Underwriting')}</strong> →
                  <strong>{t('仅限香港专业投资者认购', 'HK Professional Investors Only')}</strong> →
                  <strong>{t('资金经CIPS/QFLP通道回流', 'Funds Repatriated via CIPS/QFLP')}</strong>
                </p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              <i className="ri-information-line mr-1 text-gray-400"></i>
              {t('原始文件可在中国人民银行官网（www.pbc.gov.cn）查询，搜索"防范和处置虚拟货币交易炒作风险"。', 'The original document can be found on the People\'s Bank of China website (www.pbc.gov.cn). Search for "防范和处置虚拟货币交易炒作风险".')}
            </div>
          </div>
        )}

        {/* ── SFC合规自查清单 ── */}
        {activeHKTab === 'checklist' && (
          <div>
            <div className="bg-[#FFF8E6] dark:bg-amber-900/20 border border-[#F59E0B]/30 dark:border-amber-700/40 rounded-2xl p-5 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-checkbox-multiple-line text-[#F59E0B] text-xl"></i>
                <span className="font-black text-gray-800 dark:text-white">{t('SFC 10条合规自查清单', 'SFC 10-Point Compliance Checklist')}</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t(
                  '企业在香港启动RWA发行前，须确保以下10项全部满足。任何一项缺失均可能导致发行被叫停或面临监管处罚。',
                  'Before launching an RWA issuance in Hong Kong, enterprises must ensure all of the following items are satisfied. Any missing item may result in the issuance being halted or regulatory penalties.'
                )}
              </p>
            </div>
            <div className="space-y-3">
              {sfcChecklist.map((row, i) => (
                <div key={i} className="flex items-center gap-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl px-5 py-4">
                  <div className="w-7 h-7 flex items-center justify-center bg-[#7C3AED]/10 dark:bg-[#7C3AED]/20 rounded-full shrink-0 text-sm font-black text-[#7C3AED]">{i + 1}</div>
                  <div className="flex-1 text-sm text-gray-800 dark:text-gray-200">{row.item}</div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-bold whitespace-nowrap ${row.level === '必须' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'}`}>
                    {row.level === '必须' ? t('必须', 'Required') : t('建议', 'Recommended')}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-5 text-xs text-gray-400 text-center">
              {t('来源：SFC官方通函《有关证券型代币发行的通函》（2023.11） · 如需专业咨询请联系SFC持牌顾问', 'Source: SFC Official Circular "Circular on Security Token Offerings" (2023.11) · For professional consultation, contact an SFC-licensed advisor')}
            </div>
          </div>
        )}

        {/* 非港/CN地区正常显示 */}
        {activeRegion !== '香港' && activeRegion !== '中国内地' && (
          <div className="space-y-4">
            {active.items.map((item, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-6 hover:border-[#7C3AED]/30 transition-all">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xl">{active.flag}</span>
                    <h4 className="font-black text-gray-800 dark:text-white">{item.title}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[item.type] || 'bg-gray-100 text-gray-600'}`}>{item.type}</span>
                  </div>
                  <span className="text-xs text-gray-400 font-medium shrink-0">{item.date}</span>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
